// ObjectivesPage.js - VERSION PRODUCTION sans debug cache
import {
    Calendar,
    Clock,
    Filter,
    GitBranch,
    RefreshCw
} from 'lucide-react';
import React, { useState, useCallback, useMemo } from 'react';
import ObjectivesModal from '../modals/objectif/ObjectivesModal';

// SOLUTION : Un seul import de chaque hook au niveau parent
import { useObjectifsCached } from '../hooks-back/useObjectifsCached';
import { usePlansActionCached } from '../hooks-back/usePlansActionCached';
import { useProgressionCached } from '../hooks-back/useProgressionCached';

// Import des sections qui vont recevoir les données
import GapAnalysisSection from '../sections/gestion-des-objectifs/GapAnalysisSection';
import ProgressionStepsSection from '../sections/progression/ProgressionStepsSection';
import CalendarSection from '../sections/roadmaps/calendar/CalendarSection';
import MindmapSection from '../sections/roadmaps/mindmap/MindmapSection';
import TimelineSection from '../sections/roadmaps/timeline/TimelineSection';

const ObjectivesPage = () => {
    // 🎯 HOOKS CENTRALISÉS - UN SEUL DE CHAQUE TYPE
    const objectifsCachedHook = useObjectifsCached({
        cacheDuration: 5 * 60 * 1000, // 5 minutes
        autoRefresh: true,
        refreshInterval: 5 * 60 * 1000 // 5 minutes
    });

    const plansCachedHook = usePlansActionCached({
        cacheDuration: 5 * 60 * 1000, // 5 minutes
        autoRefresh: true,
        includeDetails: true,
        refreshInterval: 5 * 60 * 1000 // 5 minutes
    });

    const progressionCachedHook = useProgressionCached({
        cacheDuration: 5 * 60 * 1000, // 5 minutes
        autoRefresh: true,
        includeDetails: true,
        refreshInterval: 5 * 60 * 1000 // 5 minutes
    });

    // Extraire les données principales
    const {
        objectifs,
        loading: objectifsLoading,
        error: objectifsError,
        createObjectif,
        updateObjectif,
        deleteObjectif,
        refreshObjectifs,
        invalidateCache: invalidateObjectifsCache
    } = objectifsCachedHook;

    const {
        plans,
        loading: plansLoading,
        error: plansError,
        updateTaskStatus,
        refreshPlans,
        invalidateCache: invalidatePlansCache
    } = plansCachedHook;

    const {
        progressions,
        loading: progressionsLoading,
        error: progressionsError,
        refreshProgressions,
        invalidateCache: invalidateProgressionsCache
    } = progressionCachedHook;

    // États de l'interface
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeRightPanel, setActiveRightPanel] = useState('timeline');
    const [showFilters, setShowFilters] = useState(false);
    
    // Filtres Timeline et Calendar
    const [filterByYear, setFilterByYear] = useState('all');
    const [filterByCategory, setFilterByCategory] = useState('all');
    const [filterByObjective, setFilterByObjective] = useState('all');
    const [filterByPlanned, setFilterByPlanned] = useState('all');
    const [filterByStatus, setFilterByStatus] = useState('all');
    
    // Filtres spécifiques Calendar
    const [filterByMonth, setFilterByMonth] = useState('all');
    const [filterByType, setFilterByType] = useState('all');
    const [filterByPriority, setFilterByPriority] = useState('all');

    // 📊 DONNÉES CENTRALISÉES
    const centralizedData = useMemo(() => {
        return {
            objectifs: objectifs || [],
            plans: plans || [],
            progressions: progressions || {},
            isLoading: objectifsLoading || plansLoading || progressionsLoading,
            hasError: !!(objectifsError || plansError || progressionsError),
            errors: {
                objectifs: objectifsError,
                plans: plansError,
                progressions: progressionsError
            }
        };
    }, [
        objectifs, plans, progressions,
        objectifsLoading, plansLoading, progressionsLoading,
        objectifsError, plansError, progressionsError
    ]);

    // 🔄 RAFRAÎCHISSEMENT GLOBAL (fallback)
    const handleGlobalRefresh = useCallback(() => {
        invalidateObjectifsCache();
        invalidatePlansCache();
        invalidateProgressionsCache();
        
        Promise.allSettled([
            refreshObjectifs(),
            refreshPlans(),
            refreshProgressions()
        ]);
    }, [
        invalidateObjectifsCache, 
        invalidatePlansCache, 
        invalidateProgressionsCache,
        refreshObjectifs, 
        refreshPlans, 
        refreshProgressions
    ]);

    // 🧠 RAFRAÎCHISSEMENT INTELLIGENT
    const handleSmartRefresh = useCallback((actionType) => {
        switch (actionType) {
            case 'objectif_created':
            case 'objectif_updated':
                invalidateObjectifsCache();
                refreshObjectifs();
                break;
                
            case 'objectif_deleted':
                invalidateObjectifsCache();
                invalidatePlansCache();
                Promise.allSettled([refreshObjectifs(), refreshPlans()]);
                break;
                
            case 'plan_updated':
            case 'plan_created':
            case 'plan_deleted':
            case 'task_updated':
                invalidatePlansCache();
                refreshPlans();
                break;
                
            case 'progression_updated':
                invalidateProgressionsCache();
                refreshProgressions();
                break;
                
            default:
                handleGlobalRefresh();
        }
    }, [
        invalidateObjectifsCache, 
        invalidatePlansCache, 
        invalidateProgressionsCache,
        refreshObjectifs, 
        refreshPlans, 
        refreshProgressions,
        handleGlobalRefresh
    ]);

    // 📡 HOOKS CENTRALISÉS
    const centralizedHooks = useMemo(() => ({
        objectifsCachedHook,
        plansCachedHook,
        progressionCachedHook,
        refreshAll: handleGlobalRefresh,
        smartRefresh: handleSmartRefresh,
        refreshObjectifs,
        refreshPlans,
        refreshProgressions
    }), [
        objectifsCachedHook,
        plansCachedHook,
        progressionCachedHook,
        handleGlobalRefresh,
        handleSmartRefresh,
        refreshObjectifs,
        refreshPlans,
        refreshProgressions
    ]);

    // Fonctions de filtres
    const resetFilters = useCallback(() => {
        if (activeRightPanel === 'timeline') {
            setFilterByYear('all');
            setFilterByCategory('all');
            setFilterByObjective('all');
            setFilterByPlanned('all');
            setFilterByStatus('all');
        } else if (activeRightPanel === 'calendar') {
            setFilterByMonth('all');
            setFilterByCategory('all');
            setFilterByType('all');
            setFilterByStatus('all');
            setFilterByPriority('all');
        } else if (activeRightPanel === 'mindmap') {
            setFilterByYear('all');
            setFilterByCategory('all');
            setFilterByStatus('all');
        }
    }, [activeRightPanel]);

    const hasActiveFilters = useCallback(() => {
        if (activeRightPanel === 'timeline') {
            return filterByYear !== 'all' ||
                filterByCategory !== 'all' ||
                filterByObjective !== 'all' ||
                filterByPlanned !== 'all' ||
                filterByStatus !== 'all';
        } else if (activeRightPanel === 'calendar') {
            return filterByMonth !== 'all' ||
                filterByCategory !== 'all' ||
                filterByType !== 'all' ||
                filterByStatus !== 'all' ||
                filterByPriority !== 'all';
        } else if (activeRightPanel === 'mindmap') {
            return filterByYear !== 'all' ||
                filterByCategory !== 'all' ||
                filterByStatus !== 'all';
        }
        return false;
    }, [activeRightPanel, filterByYear, filterByCategory, filterByObjective, filterByPlanned, filterByStatus, filterByMonth, filterByType, filterByPriority]);

    const getActiveFiltersCount = useCallback(() => {
        if (activeRightPanel === 'timeline') {
            return [
                filterByYear !== 'all',
                filterByCategory !== 'all',
                filterByObjective !== 'all',
                filterByPlanned !== 'all',
                filterByStatus !== 'all'
            ].filter(Boolean).length;
        } else if (activeRightPanel === 'calendar') {
            return [
                filterByMonth !== 'all',
                filterByCategory !== 'all',
                filterByType !== 'all',
                filterByStatus !== 'all',
                filterByPriority !== 'all'
            ].filter(Boolean).length;
        } else if (activeRightPanel === 'mindmap') {
            return [
                filterByYear !== 'all',
                filterByCategory !== 'all',
                filterByStatus !== 'all'
            ].filter(Boolean).length;
        }
        return 0;
    }, [activeRightPanel, filterByYear, filterByCategory, filterByObjective, filterByPlanned, filterByStatus, filterByMonth, filterByType, filterByPriority]);

    // 🎯 FONCTIONS CRUD OPTIMISÉES
    const handleCreateObjective = useCallback(async (objectiveData) => {
        try {
            const result = await createObjectif({
                title: objectiveData.title || objectiveData.category,
                category: objectiveData.category,
                current_value: parseFloat(objectiveData.current_value) || 0,
                target_value: parseFloat(objectiveData.target_value),
                value_unit: objectiveData.value_unit || '',
                description: objectiveData.description || '',
                priority: objectiveData.priority || 'medium',
                timeline: objectiveData.timeline || 12,
                timeline_unit: 'mois'
            });

            if (result.success) {
                handleSmartRefresh('objectif_created');
                return result;
            }
            return result;
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [createObjectif, handleSmartRefresh]);

    const handleUpdateObjective = useCallback(async (objectiveId, updateData) => {
        try {
            const result = await updateObjectif(objectiveId, updateData);
            
            if (result.success) {
                handleSmartRefresh('objectif_updated');
                return result;
            }
            return result;
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [updateObjectif, handleSmartRefresh]);

    const handleDeleteObjective = useCallback(async (objectiveId) => {
        try {
            const result = await deleteObjectif(objectiveId);
            
            if (result.success) {
                handleSmartRefresh('objectif_deleted');
                return result;
            }
            return result;
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [deleteObjectif, handleSmartRefresh]);

    const handleUpdateTaskStatus = useCallback(async (...args) => {
        try {
            const result = await updateTaskStatus(...args);
            
            if (result && result.success) {
                handleSmartRefresh('task_updated');
            }
            
            return result;
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [updateTaskStatus, handleSmartRefresh]);

    // 🎨 PROPS COMMUNES
    const commonComponentProps = useMemo(() => ({
        data: centralizedData,
        hooks: centralizedHooks,
        onGlobalRefresh: handleGlobalRefresh,
        onSmartRefresh: handleSmartRefresh,
        onCreateObjective: handleCreateObjective,
        onUpdateObjective: handleUpdateObjective,
        onDeleteObjective: handleDeleteObjective,
        updateTaskStatus: handleUpdateTaskStatus,
        showFilters,
        onFilterReset: resetFilters
    }), [
        centralizedData,
        centralizedHooks,
        handleGlobalRefresh,
        handleSmartRefresh,
        handleCreateObjective,
        handleUpdateObjective,
        handleDeleteObjective,
        handleUpdateTaskStatus,
        showFilters,
        resetFilters
    ]);

    // Fonction pour rendre le contenu selon la vue active
    const renderActivePanel = useCallback(() => {
        const panelProps = {
            ...commonComponentProps,
            filterProps: {
                filterByYear,
                filterByCategory,
                filterByObjective,
                filterByPlanned,
                filterByStatus,
                filterByMonth,
                filterByType,
                filterByPriority,
                onFilterYearChange: setFilterByYear,
                onFilterCategoryChange: setFilterByCategory,
                onFilterObjectiveChange: setFilterByObjective,
                onFilterPlannedChange: setFilterByPlanned,
                onFilterStatusChange: setFilterByStatus,
                onFilterMonthChange: setFilterByMonth,
                onFilterTypeChange: setFilterByType,
                onFilterPriorityChange: setFilterByPriority
            }
        };

        switch (activeRightPanel) {
            case 'timeline':
                return <TimelineSection {...panelProps} />;
            case 'mindmap':
                return <MindmapSection {...panelProps} className="h-full" />;
            case 'calendar':
                return <CalendarSection {...panelProps} className="h-full" />;
            default:
                return <div className="flex items-center justify-center h-full text-gray-500">Vue non supportée</div>;
        }
    }, [
        activeRightPanel,
        commonComponentProps,
        filterByYear, filterByCategory, filterByObjective, filterByPlanned, filterByStatus,
        filterByMonth, filterByType, filterByPriority
    ]);

    // États de chargement et d'erreur
    if (centralizedData.isLoading && !centralizedData.objectifs.length) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#016666] mx-auto mb-4"></div>
                        <p className="text-gray-500">Chargement des données...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (centralizedData.hasError && !centralizedData.objectifs.length) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                        <div className="text-red-500 mb-4">⚠️</div>
                        <p className="text-gray-900 font-medium mb-2">Erreur de chargement</p>
                        <div className="text-gray-500 text-sm mb-4">
                            {Object.entries(centralizedData.errors)
                                .filter(([, error]) => error)
                                .map(([type, error]) => (
                                    <div key={type}>{type}: {error.message || error}</div>
                                ))
                            }
                        </div>
                        <button
                            onClick={handleGlobalRefresh}
                            className="px-4 py-2 bg-[#016666] text-white rounded-lg hover:bg-[#014d4d]"
                        >
                            Réessayer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Progression Steps Section */}
            <div className="">
                <ProgressionStepsSection 
                    objectives={centralizedData.objectifs}
                    data={centralizedData}
                    hooks={centralizedHooks}
                    onGlobalRefresh={handleGlobalRefresh}
                    onSmartRefresh={handleSmartRefresh}
                />
            </div>

            {/* Gap Analysis and Timeline/Mindmap/Calendar Section */}
            <div className="flex flex-col lg:flex-row gap-6 h-[770px]">
                {/* Gap Analysis Section */}
                <div className="w-full lg:w-1/2 h-full">
                    <GapAnalysisSection 
                        {...commonComponentProps}
                    />
                </div>

                {/* Right half avec hauteur fixe et scroll */}
                <div className="w-full lg:w-1/2 h-full bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
                    {/* Header fixe */}
                    <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex-shrink-0 rounded-t-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center text-gray-700">
                                <h2 className="text-sm font-semibold">Feuille de route</h2>
                                {centralizedData.isLoading && (
                                    <RefreshCw className="w-3 h-3 ml-2 animate-spin text-gray-500" />
                                )}
                            </div>
                            <div className="flex items-center space-x-2">
                                {/* Bouton Filtrer */}
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center px-2 py-1 transition-colors rounded-md text-xs ${
                                        showFilters || hasActiveFilters()
                                            ? 'bg-gray-200 text-gray-700'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    <Filter className="w-3 h-3 mr-1" />
                                    Filtrer
                                    {hasActiveFilters() && (
                                        <span className="ml-1 px-1 py-0.5 text-xs bg-emerald-600 text-white rounded-full">
                                            {getActiveFiltersCount()}
                                        </span>
                                    )}
                                </button>

                                {/* Boutons pour basculer entre Timeline, Calendar et Mind Map */}
                                <div className="flex items-center bg-gray-200 rounded-md p-0.5">
                                    <button
                                        onClick={() => setActiveRightPanel('timeline')}
                                        className={`flex items-center px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                                            activeRightPanel === 'timeline'
                                                ? "text-white shadow-sm"
                                                : "text-gray-700 hover:bg-gray-300"
                                        }`}
                                        style={activeRightPanel === 'timeline' ? { backgroundColor: '#016666' } : {}}
                                    >
                                        <Clock className="w-3 h-3 mr-1" />
                                        Timeline
                                    </button>
                                    <button
                                        onClick={() => setActiveRightPanel('calendar')}
                                        className={`flex items-center px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                                            activeRightPanel === 'calendar'
                                                ? "text-white shadow-sm"
                                                : "text-gray-700 hover:bg-gray-300"
                                        }`}
                                        style={activeRightPanel === 'calendar' ? { backgroundColor: '#016666' } : {}}
                                    >
                                        <Calendar className="w-3 h-3 mr-1" />
                                        Calendrier
                                    </button>
                                    <button
                                        onClick={() => setActiveRightPanel('mindmap')}
                                        className={`flex items-center px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                                            activeRightPanel === 'mindmap'
                                                ? "text-white shadow-sm"
                                                : "text-gray-700 hover:bg-gray-300"
                                        }`}
                                        style={activeRightPanel === 'mindmap' ? { backgroundColor: '#016666' } : {}}
                                    >
                                        <GitBranch className="w-3 h-3 mr-1" />
                                        Mind Map
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contenu scrollable */}
                    <div className="flex-1 overflow-auto">
                        {renderActivePanel()}
                    </div>
                </div>
            </div>

            {/* Modal */}
            <ObjectivesModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleCreateObjective}
            />
        </div>
    );
};

export default ObjectivesPage;